import React from 'react';
import Colors from '../../../constants/Colors';
import Point from './Point';
import {
  alternateComparedLineColor, alternateLineColor, selectedLineColor,
} from '../../AirQualityGraph';
import { comparisonLineColor } from '../../../components/Home/HomeGraphLineSelector';
import { ChartDataPoint, tooltipDateLabel } from '../../axisUtils';

interface TooltipProps {
  chartData: ChartDataPoint[],
  comparisonData: ChartDataPoint[],
  alternateData: ChartDataPoint[],
  alternateComparisonData: ChartDataPoint[],
  title: string,
  alternateTitle?: string,
  paddingVertical: number,
  chartHeight: number,
  domainLimit: number,
  // eslint-disable-next-line no-unused-vars
  onClick: (date: Date) => void,
  x?: number,
  y?: number,
  datum?,
  unit?: string,
  alternateColor?: string,
  hideTimeOnTooltip?: boolean,
}

const Tooltip = ({
  chartData, comparisonData, alternateData, title, alternateTitle, paddingVertical, chartHeight,
  domainLimit, x, y, datum, onClick, unit, alternateColor, hideTimeOnTooltip,
  alternateComparisonData,
}: TooltipProps) => {
  const unitSuffix: string = ` ${unit?.toLowerCase() ?? ''}`;
  const date: Date = ((chartData && chartData[datum.index]?.date)
    || (alternateData && alternateData[datum.index]?.date))
    ? new Date(chartData[datum.index]?.date || alternateData[datum.index]?.date) : null;
  const dateCompared: Date = (comparisonData && comparisonData[datum.index]?.date)
    ? new Date(comparisonData[datum.index]?.date) : null;
  const alternateDateCompared: Date = (alternateComparisonData
    && alternateComparisonData[datum.index]?.date)
    ? new Date(alternateComparisonData[datum.index]?.date) : null;
  const largerDataset = (alternateData && alternateData?.length > chartData?.length)
    ? alternateData : chartData;
  const dateText: string = date
    ? tooltipDateLabel(largerDataset, datum.index, false, !hideTimeOnTooltip) : '-';
  const dateComparedText: string = dateCompared
    ? `vs. ${tooltipDateLabel(comparisonData, datum.index, false, !hideTimeOnTooltip)}` : null;
  const alternateDateComparedText: string = alternateDateCompared
    ? `vs. ${tooltipDateLabel(alternateComparisonData, datum.index, false, !hideTimeOnTooltip)}`
    : null;
  let valueText: string = `${title}: ${(chartData && chartData[datum.index])
    ?.value ? `${(Math.floor(chartData[datum.index].value * 10) / 10)}` : '-'}`;
  valueText += unitSuffix;
  let alternateValueText: string = `${alternateTitle}: ${(alternateData && alternateData[datum.index])
    ?.value ? `${(Math.floor(alternateData[datum.index].value * 10) / 10)}` : '-'}`;
  alternateValueText += unitSuffix;
  let valueComparedText: string = `${title} (compared): ${(comparisonData
    && comparisonData[datum.index])
    ?.value ? `${(Math.floor(comparisonData[datum.index].value * 10) / 10)}` : '-'}`;
  valueComparedText += unitSuffix;
  let alternateValueComparedText: string = `${alternateTitle} (compared): ${(alternateComparisonData
    && alternateComparisonData[datum.index])
    ?.value ? `${(Math.floor(alternateComparisonData[datum.index].value * 10) / 10)}` : '-'}`;
  alternateValueComparedText += unitSuffix;
  let containerHeight: number = comparisonData ? 96 : 56;
  if (alternateData) containerHeight += comparisonData ? 40 : 20;
  const containerWidth: number = (comparisonData ? 100 : 50)
    + Math.max(
      valueText.length,
      dateText.length,
      (comparisonData && comparisonData[datum.index]?.date) ? dateComparedText.length : 0,
      comparisonData ? valueComparedText.length : 0,
      alternateData ? alternateValueText.length : 0,
    ) * 5.5;
  const containerX: number = x > containerWidth + 20 ? x - containerWidth - 10 : x + 10;
  let containerY: number = Math.max(Math.min(y - containerHeight, 210 - containerHeight), 10);
  if (!Number.isFinite(containerY)) containerY = 10;

  const spot = (cy: number, fill: string) => <circle
    r={6}
    cx={containerX + 15}
    cy={containerY + cy}
    {...{ fill }}
  />;

  const label = (cy: number, text: string) => <text
    x={containerX + 30}
    y={containerY + cy}
    fontFamily={'Roboto_400Regular'}
    fontSize={12}
    height={16}
    strokeWidth={0}
    letterSpacing={0.15}
    fill={Colors.light.hereworksBlack400}>
    {text}
  </text>;

  const dateLabel = (cy: number, text: string) => <text
    x={containerX + 10}
    y={containerY + cy}
    fontFamily={'Poppins_500Medium'}
    fontSize={12}
    height={16}
    strokeWidth={0}
    letterSpacing={0.15}
    fill={Colors.light.hereworksBlack400}>
    {text}
  </text>;

  return <svg>
    <defs>
      <filter id={'f3_desk_bookings'} x={-0.5} y={-1} width='200%' height='250%'>
        <feOffset result='offOut' in='SourceGraphic' dx='0' dy='0' />
        <feColorMatrix result='matrixOut'
          in='offOut'
          type='matrix'
          values='0.1 0 0 0 0 0 0.1 0 0 0 0 0 0.1 0 0 0 0 0 0.2 0' />
        <feGaussianBlur result='blurOut' in='matrixOut' stdDeviation='4' />
        <feBlend in='SourceGraphic' in2='blurOut' mode='normal' result='colored' />
      </filter>
    </defs>
    <Point
      cx={x}
      value={chartData[datum.index]?.value}
      stroke={selectedLineColor}
      limit={domainLimit}
      onClick={onClick ? () => onClick(date) : null}
      {...{ paddingVertical, chartHeight }}
    />
    {alternateData && <Point
      cx={x}
      value={alternateData[datum.index]?.value}
      stroke={alternateColor ?? alternateLineColor}
      limit={domainLimit}
      onClick={onClick ? () => onClick(dateCompared) : null}
      {...{ paddingVertical, chartHeight }}
    />}
    {comparisonData && <Point
      cx={x}
      value={comparisonData[datum.index]?.value}
      stroke={comparisonLineColor}
      limit={domainLimit}
      onClick={onClick ? () => onClick(date) : null}
      {...{ paddingVertical, chartHeight }}
    />}
    {alternateComparisonData && <Point
      cx={x}
      value={alternateComparisonData[datum.index]?.value}
      stroke={alternateComparedLineColor}
      limit={domainLimit}
      onClick={onClick ? () => onClick(date) : null}
      {...{ paddingVertical, chartHeight }}
    />}
    <rect
      x={containerX}
      y={containerY}
      width={containerWidth}
      height={containerHeight}
      stroke={selectedLineColor}
      rx={5}
      fill='white'
      filter={'url(#f3_desk_bookings)'}
    />
    {datum.date && dateLabel(20, dateText)}
    {comparisonData ? (comparisonData[datum.index]?.date && dateLabel(40, dateComparedText))
      : (alternateComparisonData && alternateComparisonData[datum.index]?.date
        && dateLabel(40, alternateDateComparedText))}
    <line
      x1={x}
      x2={x}
      y1={paddingVertical}
      y2={chartHeight - paddingVertical + 15}
      stroke={Colors.light.hereworksBlue100}
      strokeDasharray='10,10'
      strokeWidth={1}
    />
    {spot(comparisonData ? 60.5 : 40.5, selectedLineColor)}
    {label(comparisonData ? 65 : 45, valueText)}
    {alternateData && <>
      {spot(80.5, alternateColor ?? alternateLineColor)}
      {label(85, alternateValueText)}
    </>}
    {comparisonData && <>
      {spot(alternateData ? 100.5 : 80.5, comparisonLineColor)}
      {label(alternateData ? 105 : 85, valueComparedText)}
    </>}
    {alternateComparisonData && <>
      {spot(120.5, alternateComparedLineColor)}
      {label(125, alternateValueComparedText)}
    </>}
  </svg>;
};

export default Tooltip;
